import React, { useCallback, useEffect, useState } from "react";
import { API } from "../../../services/api";
import { DEFAULT_EHPAD_COLOR } from "../adminConstants";
import { EhpadCreateForm } from "./EhpadCreateForm";
import { EhpadEditor } from "./EhpadEditor";
import { InvitationCodeModal, TransferEhpadPanel } from "./EhpadInvitationParts";
import { EmailsEditor } from "./EmailsEditor";
import { INVITATION_REFRESH_MS, formatInvitationExpiryTime, invitationExpiryTime, invitationStatusColor } from "./ehpadInvitations";
import { controleModeLabel } from "./ehpadRules";
import {
    btnOutlineStyle,
    btnPurpleFilledStyle,
    btnPurpleOutlineStyle,
    cardStyle,
    cardTitleStyle,
    countBadgeStyle,
    tableStyle,
    tdStyle,
    thStyle,
} from "../styles/adminDashboardStyles";


// Raccordements et invitations des EHPAD : état relu à l'ouverture, après chaque invitation et toutes les 15 s
// tant qu'une invitation est en cours.
function useEhpadInvitationState(sentInvitation) {
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [clock, setClock] = useState(() => Date.now());
    // La liste n'est chargée qu'à l'ouverture et après un envoi : on réévalue l'affichage à la prochaine expiration
    // pour que « Raccordement en cours » devienne « Raccordement expiré » sans recharger la page.
    useEffect(() => {
        const nextExpiry = pendingInvitations
            .map(invitation => invitationExpiryTime(invitation))
            .filter(time => time > clock)
            .sort((a, b) => a - b)[0];
        if (nextExpiry === undefined) return undefined;
        const timer = setTimeout(() => setClock(Date.now()), nextExpiry - Date.now() + 500);
        return () => clearTimeout(timer);
    }, [pendingInvitations, clock]);
    const [connectedEhpadIds, setConnectedEhpadIds] = useState([]);
    // EHPAD au raccordement désactivé, avec l'email du dernier raccordement (pour le réinviter).
    const [deactivatedLinks, setDeactivatedLinks] = useState([]);
    const [managedWithoutLinkIds, setManagedWithoutLinkIds] = useState([]);
    // Transferts en cours : invitations envoyées au responsable d'un EHPAD d'une autre pharmacie.
    const [transfers, setTransfers] = useState([]);

    // Invitations en cours des EHPAD non raccordés (erreurs de code, blocage à 5) et EHPAD raccordés ;
    // rechargés après chaque nouvelle invitation.
    const loadInvitationState = useCallback(() => {
        API.get("/admin/ehpads/invitations/pending")
            .then(res => setPendingInvitations(Array.isArray(res.data) ? res.data : []))
            .catch(() => setPendingInvitations([]));
        API.get("/admin/ehpads/invitations/connected")
            .then(res => setConnectedEhpadIds(Array.isArray(res.data) ? res.data : []))
            .catch(() => setConnectedEhpadIds([]));
        API.get("/admin/ehpads/invitations/deactivated")
            .then(res => setDeactivatedLinks(Array.isArray(res.data) ? res.data : []))
            .catch(() => setDeactivatedLinks([]));
        API.get("/admin/ehpads/invitations/managed-without-link")
            .then(res => setManagedWithoutLinkIds(Array.isArray(res.data) ? res.data : []))
            .catch(() => setManagedWithoutLinkIds([]));
        API.get("/admin/ehpads/invitations/transfers")
            .then(res => setTransfers(Array.isArray(res.data) ? res.data : []))
            .catch(() => setTransfers([]));
    }, []);

    useEffect(() => {
        loadInvitationState();
    }, [sentInvitation, loadInvitationState]);

    // Le portail EHPAD écrit les erreurs de code et l'acceptation directement en base : tant qu'une invitation est
    // en cours, on relit l'état toutes les 15 s (deux petites requêtes), seulement si l'onglet est visible.
    const hasLiveInvitation = [...pendingInvitations, ...transfers].some(invitation => invitationExpiryTime(invitation) > clock);
    useEffect(() => {
        if (!hasLiveInvitation) return undefined;
        const timer = setInterval(() => {
            if (typeof document === "undefined" || document.visibilityState !== "hidden") loadInvitationState();
        }, INVITATION_REFRESH_MS);
        return () => clearInterval(timer);
    }, [hasLiveInvitation, loadInvitationState]);

    return { pendingInvitations, connectedEhpadIds, deactivatedLinks, managedWithoutLinkIds, transfers, clock, loadInvitationState };
}

// Situation de raccordement d'un EHPAD. Le serveur ne renvoie que la dernière invitation de chaque EHPAD, expirée
// comprise ; un raccordement désactivé laisse la place à une réinvitation en cours ; un responsable créé par le portail
// sans lien permet à la pharmacie de créer le lien.
export function ehpadConnection(ehpad, invitations) {
    const lastInvitation = invitations.pendingInvitations.find(invitation => invitation.ehpadId === ehpad.id);
    const pendingInvitation = lastInvitation
        ? { ...lastInvitation, expired: invitationExpiryTime(lastInvitation) <= invitations.clock }
        : null;
    const connected = invitations.connectedEhpadIds.includes(ehpad.id);
    const deactivatedLink = connected ? null : invitations.deactivatedLinks.find(link => link.ehpadId === ehpad.id);
    const deactivated = Boolean(deactivatedLink);
    const managedWithoutLink = !connected && invitations.managedWithoutLinkIds.includes(ehpad.id);
    let kind = "none";
    if (deactivated) kind = "deactivated";
    else if (connected) kind = "connected";
    else if (managedWithoutLink) kind = "manager";
    return { pendingInvitation, connected, deactivatedLink, deactivated, managedWithoutLink, kind };
}

function EhpadConnectionStatus({ ehpad: e, connection }) {
    const { pendingInvitation, connected, deactivated, managedWithoutLink } = connection;
    return (
        <>
                {(connected || (deactivated && !pendingInvitation)) && (
                    <div
                        data-testid={`ehpad-${e.id}-connection-status`}
                        style={{ marginTop: "5px", fontSize: "11px", fontWeight: 600, color: deactivated ? "#6b7280" : "#15803d" }}
                    >
                        {deactivated ? "Raccordement désactivé" : "✓ Raccordé"}
                    </div>
                )}
                {managedWithoutLink && !pendingInvitation && (
                    <div
                        data-testid={`ehpad-${e.id}-connection-status`}
                        style={{ marginTop: "5px", fontSize: "11px", fontWeight: 600, color: "#92400e" }}
                    >
                        Responsable sur le portail · non raccordé
                    </div>
                )}
                {!connected && pendingInvitation && (
                    <div
                        data-testid={`ehpad-${e.id}-invitation-status`}
                        title={`Invitation envoyée à ${pendingInvitation.email}`}
                        style={{ marginTop: "5px", fontSize: "11px", fontWeight: 600, color: invitationStatusColor(pendingInvitation) }}
                    >
                        {pendingInvitation.expired ? "Raccordement expiré" : (
                            <>
                                {pendingInvitation.blocked ? "Raccordement bloqué" : "Raccordement en cours"}
                                {" · "}
                                {pendingInvitation.failedAttempts}/{pendingInvitation.maxFailedAttempts ?? 5} erreur{pendingInvitation.failedAttempts > 1 ? "s" : ""} de code
                                {" · expire à "}
                                {formatInvitationExpiryTime(pendingInvitation)}
                            </>
                        )}
                    </div>
                )}
        </>
    );
}

function EhpadIdentityCell({ ehpad: e, connection }) {
    return (
        <td style={tdStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                <span
                    title={e.color || DEFAULT_EHPAD_COLOR}
                    style={{
                        display: "inline-block",
                        width: "28px",
                        height: "28px",
                        flexShrink: 0,
                        borderRadius: "7px",
                        background: e.color || DEFAULT_EHPAD_COLOR,
                        border: "1px solid #cbd5e1",
                    }}
                />
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis" }}>{e.nom}</div>
                    <div style={{ color: "#6c4de6", fontWeight: 700, fontSize: "11px", marginTop: "2px" }}>{e.code}</div>
                    <EhpadConnectionStatus ehpad={e} connection={connection} />
                    {(e.productionSubcategories ?? []).length > 0 && (
                        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginTop: "5px" }}>
                            {e.productionSubcategories.slice(0, 3).map(subcategory => (
                                <span key={subcategory} style={{ padding: "2px 6px", borderRadius: "6px", background: "#eff6ff", color: "#1d4ed8", fontSize: "11px", fontWeight: 700 }}>
                                    {subcategory}
                                </span>
                            ))}
                            {e.productionSubcategories.length > 3 && (
                                <span style={{ color: "#64748b", fontSize: "11px", fontWeight: 700 }}>+{e.productionSubcategories.length - 3}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </td>
    );
}

// Nombre de patients et d'emails de l'EHPAD.
function EhpadFollowUpCell({ ehpad: e }) {
    const total = (e.emailsContact ?? []).length + (e.emailsFax ?? []).length;
    const patientCount = e.patientCount ?? 0;
    return (
        <td style={tdStyle}>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 9px", borderRadius: "20px", background: patientCount > 0 ? "#dcfce7" : "#f3f4f6", color: patientCount > 0 ? "#166534" : "#9ca3af", fontSize: "12px", fontWeight: 500, whiteSpace: "nowrap" }}>
                {patientCount} patient{patientCount > 1 ? "s" : ""}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 9px", borderRadius: "20px", background: total > 0 ? "#ede9fe" : "#f3f4f6", color: total > 0 ? "#4c1d95" : "#9ca3af", fontSize: "12px", fontWeight: 500, whiteSpace: "nowrap" }}>
                    ✉ {total}
                </span>
            </div>
        </td>
    );
}

// Coordonnées, tournée, mode de contrôle et suivi d'un EHPAD.
function EhpadDetailsCells({ ehpad: e }) {
    return (
        <>
            <td style={tdStyle}>
                <div style={{ color: e.address ? "#374151" : "#d1d5db", fontSize: "13px", lineHeight: 1.35 }}>
                    {e.address ?? "Adresse non renseignée"}
                </div>
                <div style={{ color: e.phone ? "#64748b" : "#d1d5db", fontSize: "12px", marginTop: "3px" }}>
                    {e.phone ?? "Téléphone non renseigné"}
                </div>
            </td>
            <td style={tdStyle}>
                {e.tournee ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "13px", color: "#374151" }}>
                        <span style={{ color: "#6c4de6" }}>⏱</span>
                        {e.tournee}
                    </span>
                ) : (
                    <span style={{ color: "#d1d5db" }}>—</span>
                )}
            </td>
            <td style={tdStyle}>
                <span style={{ display: "inline-flex", padding: "3px 9px", borderRadius: "20px", background: e.controleMode ? "#e0f2fe" : "#f3f4f6", color: e.controleMode ? "#075985" : "#9ca3af", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>
                    {controleModeLabel(e.controleMode)}
                </span>
            </td>
            <EhpadFollowUpCell ehpad={e} />
        </>
    );
}

function EhpadActionsCell({ editOpen, emailsOpen, onToggleEdit, onToggleEmails }) {
    return (
        <td style={{ ...tdStyle, width: "1%", whiteSpace: "nowrap", textAlign: "right" }}>
            <div style={{ display: "inline-flex", gap: "6px" }}>
            <button
                onClick={onToggleEdit}
                style={editOpen ? { ...btnOutlineStyle, background: "#f3f4f6" } : btnOutlineStyle}
            >
                {editOpen ? "Fermer" : "Éditer"}
            </button>
            <button
                onClick={onToggleEmails}
                style={emailsOpen ? btnPurpleFilledStyle : btnPurpleOutlineStyle}
            >
                {emailsOpen ? "Fermer" : "Emails"}
            </button>
            </div>
        </td>
    );
}

// Ligne d'un EHPAD, suivie de son éditeur ou de ses emails quand ils sont ouverts.
function EhpadRow({ ehpad: e, connection, editOpen, emailsOpen, tournees, onToggleEdit, onToggleEmails, editorProps, onEmailsSaved }) {
    return (
        <>
            <tr style={{ borderBottom: editOpen || emailsOpen ? "none" : "1px solid #f3f4f6" }}>
                <EhpadIdentityCell ehpad={e} connection={connection} />
                <EhpadDetailsCells ehpad={e} />
                <EhpadActionsCell editOpen={editOpen} emailsOpen={emailsOpen} onToggleEdit={onToggleEdit} onToggleEmails={onToggleEmails} />
            </tr>
            {editOpen && (
                <tr>
                    <td colSpan={6} style={{ padding: 0, borderBottom: "1px solid #f3f4f6" }}>
                        <EhpadEditor
                            ehpad={e}
                            tournees={tournees}
                            pendingInvitation={connection.connected ? null : connection.pendingInvitation}
                            connection={connection.kind}
                            deactivatedEmail={connection.deactivatedLink?.email ?? null}
                            {...editorProps}
                        />
                    </td>
                </tr>
            )}
            {emailsOpen && (
                <tr>
                    <td colSpan={6} style={{ padding: 0, borderBottom: "1px solid #f3f4f6" }}>
                        <EmailsEditor ehpad={e} onSaved={onEmailsSaved} />
                    </td>
                </tr>
            )}
        </>
    );
}

export function EhpadsTab() {
    const [ehpads, setEhpads] = useState([]);
    const [tournees, setTournees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sentInvitation, setSentInvitation] = useState(null);
    const [editingEmails, setEditingEmails] = useState(null);
    const [editingEhpad, setEditingEhpad] = useState(null);

    const invitations = useEhpadInvitationState(sentInvitation);
    const { transfers, clock, loadInvitationState } = invitations;

    useEffect(() => {
        Promise.all([
            API.get("/admin/ehpads"),
            API.get("/admin/tournees"),
        ]).then(([ehpadsRes, tourneesRes]) => {
            setEhpads(ehpadsRes.data);
            setTournees(tourneesRes.data);
        }).finally(() => setLoading(false));
    }, []);

    const handleEmailsSaved = (updatedEhpad) => {
        setEhpads(prev => prev.map(e => e.id === updatedEhpad.id ? updatedEhpad : e));
        setEditingEmails(null);
    };

    // Rafraîchit la ligne sans fermer l'éditeur (enregistrement partiel).
    const handleEhpadUpdated = (updatedEhpad) => {
        setEhpads(prev => prev.map(e => e.id === updatedEhpad.id ? updatedEhpad : e));
    };

    const handleEhpadSaved = (updatedEhpad) => {
        setEhpads(prev => prev.map(e => e.id === updatedEhpad.id ? updatedEhpad : e));
        setEditingEhpad(null);
    };

    const handleEhpadDeleted = (id) => {
        setEhpads(prev => prev.filter(e => e.id !== id));
        setEditingEhpad(null);
    };

    return (
        <div>
            {/* ── Formulaire création ──────────────────────────────────── */}
            <div style={cardStyle}>
                <h2 style={cardTitleStyle}>Ajouter un EHPAD</h2>
                <EhpadCreateForm
                    ehpads={ehpads}
                    tournees={tournees}
                    onCreated={created => setEhpads(prev => [...prev, created])}
                    onInvited={setSentInvitation}
                />
            </div>
            {/* ── Transfert d'un EHPAD d'une autre pharmacie ───────────── */}
            <div style={cardStyle}>
                <h2 style={cardTitleStyle}>Transférer un EHPAD d'une autre pharmacie</h2>
                <TransferEhpadPanel transfers={transfers} clock={clock} onInvited={setSentInvitation} onChanged={loadInvitationState} />
            </div>
            {sentInvitation && (
                <InvitationCodeModal invitation={sentInvitation} onClose={() => setSentInvitation(null)} />
            )}

            {/* ── Liste EHPADs ─────────────────────────────────────────── */}
            <div style={cardStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
                    <h2 style={{ ...cardTitleStyle, margin: 0 }}>EHPADs</h2>
                    {!loading && <span style={countBadgeStyle}>{ehpads.length}</span>}
                </div>
                {loading ? (
                    <p style={{ color: "#9ca3af", fontSize: "14px" }}>Chargement...</p>
                ) : (
                    <div style={{ width: "100%", overflowX: "auto" }}>
                    <table style={{ ...tableStyle, minWidth: "760px" }}>
                        <thead>
                            <tr>
                                <th style={{ ...thStyle, width: "28%" }}>EHPAD</th>
                                <th style={{ ...thStyle, width: "30%" }}>Coordonnées</th>
                                <th style={thStyle}>Tournée</th>
                                <th style={thStyle}>Contrôle</th>
                                <th style={thStyle}>Suivi</th>
                                <th style={{ ...thStyle, textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ehpads.length === 0 && (
                                <tr><td colSpan={6} style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>Aucun EHPAD</td></tr>
                            )}
                            {ehpads.map(e => (
                                <EhpadRow
                                    key={e.id}
                                    ehpad={e}
                                    connection={ehpadConnection(e, invitations)}
                                    editOpen={editingEhpad === e.id}
                                    emailsOpen={editingEmails === e.id}
                                    tournees={tournees}
                                    onToggleEdit={() => { setEditingEhpad(editingEhpad === e.id ? null : e.id); setEditingEmails(null); }}
                                    onToggleEmails={() => { setEditingEmails(editingEmails === e.id ? null : e.id); setEditingEhpad(null); }}
                                    editorProps={{
                                        onInvited: setSentInvitation,
                                        onConnectionChanged: loadInvitationState,
                                        onUpdated: handleEhpadUpdated,
                                        onSaved: handleEhpadSaved,
                                        onDeleted: handleEhpadDeleted,
                                        onCancel: () => setEditingEhpad(null),
                                    }}
                                    onEmailsSaved={handleEmailsSaved}
                                />
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>
        </div>
    );
}
